import React, { useRef, useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';

import { Modal } from '../../custom-essentials';
import AppointmentModalBodyFooter from './AppointmentsModalBF';
import { checkResponse } from '../../form';
import { getymd, getymdhms, formatDate } from '../../functions';

import {
    fetchMpCentersAll,
    fetchRpCentersAll,
    fetchStudentsAll,
    fetchMembersMpPermissions,
    fetchAppointmentsIds,
    fetchCycleGroupsActive,
    fetchContractsActiveStudents,
    createAppointment,
    updateAppointmentCore,
    deleteAppointment,
    deleteAppointmentForce
} from '../../../actions';

// Portal detection
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isReadingPlex = hostname.includes('readingplex');

function AppointmentModal(props) {
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });

    const [showModal, setShowModal] = useState(true);
    const [loaded, setLoaded] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    // Default null to match 'undefined' from not passing selectedAppointment to begin with
    const [appointment, setAppointment] = useState({});
    const [parents, setParents] = useState([]);
    const [centerOptions, setCenterOptions] = useState([]);
    const [studentOptions, setStudentOptions] = useState([]);
    const [allStudents, setAllStudents] = useState([]);
    // ReadingPlex-specific: Group options
    const [groupOptions, setGroupOptions] = useState([]);
    const [groups, setGroups] = useState([]);
    // ReadingPlex-specific: Contract options
    const [contractOptions, setContractOptions] = useState([]);
    const [allContracts, setAllContracts] = useState([]); // All contracts for filtering
    const [contractsByStudent, setContractsByStudent] = useState({}); // Contracts mapped by student ID
    const studentContractsRef = useRef({}); // Use ref to avoid stale closure issues
    const [studentContracts, setStudentContracts] = useState({}); // Track each student's selected contract in group appointments

    // Sync ref with state to avoid stale closures in handleSubmit
    useEffect(() => {
        studentContractsRef.current = studentContracts;
        console.log('[STATE_SYNC] studentContracts state updated, ref synced:', Object.keys(studentContracts).length, 'students');
    }, [studentContracts]);

    const { mode, fetchMpCentersAll, fetchRpCentersAll, fetchStudentsAll, fetchMembersMpPermissions,
        fetchAppointmentsIds, fetchCycleGroupsActive, fetchContractsActiveStudents, createAppointment, updateAppointmentCore,
        deleteAppointment, deleteAppointmentForce, selectedDate, selectedCenter, selectedAppointment,
        onSubmitCallback } = props;

    const extractArray = (response, preferredKeys = []) => {
        if (!response) return [];
        if (Array.isArray(response)) return response;
        if (Array.isArray(response?.data)) return response.data;
        for (const key of preferredKeys) {
            if (Array.isArray(response?.data?.[key])) return response.data[key];
            if (Array.isArray(response?.[key])) return response[key];
        }
        return [];
    };

    useEffect(() => {
        async function init() {
            // Use portal-specific center fetch
            const centersRes = isReadingPlex ? await fetchRpCentersAll() : await fetchMpCentersAll();
            const studentsRes = await fetchStudentsAll();
            const studentUserRes = await fetchMembersMpPermissions({ permissions: 'Student' });
            const newCenters = extractArray(centersRes, ['centers']);
            const newStudents = extractArray(studentsRes, ['students']);
            const newStudentUsers = extractArray(studentUserRes, ['users', 'members', 'students']);

            // For ReadingPlex, fetch cycle groups
            let newGroups = [];
            let newGroupOptions = [];
            if (isReadingPlex) {
                const groupsRes = await fetchCycleGroupsActive();
                newGroups = extractArray(groupsRes, ['groups']);
                // Extract groupMembers from response - they come separately from groups
                const groupMembers = extractArray(groupsRes, ['groupMembers']);

                // Map group members to their groups
                const groupMembersMap = {};
                groupMembers.forEach(gm => {
                    const groupId = parseInt(gm.group_id);
                    if (!groupMembersMap[groupId]) {
                        groupMembersMap[groupId] = [];
                    }
                    // Store student ID (UUID string)
                    groupMembersMap[groupId].push(String(gm.student));
                });

                newGroupOptions = [
                    { value: -1, label: 'All Groups' },
                    ...newGroups.map(g => ({
                        value: g.id,
                        label: g.name,
                        students: groupMembersMap[parseInt(g.id)] || [], // Get students from groupMembers map
                        center: g.center,
                        schedulingTime: g.time, // Use 'time' field from database
                        schedulingDays: (() => {
                            // Build scheduling days string from day flags
                            const days = [];
                            if (parseInt(g.sundays) === 1) days.push('Sun');
                            if (parseInt(g.mondays) === 1) days.push('Mon');
                            if (parseInt(g.tuesdays) === 1) days.push('Tue');
                            if (parseInt(g.wednesdays) === 1) days.push('Wed');
                            if (parseInt(g.thursdays) === 1) days.push('Thu');
                            if (parseInt(g.fridays) === 1) days.push('Fri');
                            if (parseInt(g.saturdays) === 1) days.push('Sat');
                            return days.join(', ') || 'No days set';
                        })()
                    }))
                ];
            }

            const newCenterOptions = newCenters.map(c => ({
                value: c?.id !== undefined ? parseInt(c.id, 10) : c.id,
                label: c.name || 'Unknown Center'
            }));

            // Filter students based on portal
            const filteredStudents = newStudents.filter(s => {
                if (parseInt(s.is_lead) !== 0) return false;
                if (isReadingPlex) {
                    return (parseInt(s.is_rp_student) === 1 && parseInt(s.rp_active) === 1)
                        || s.user_id === selectedAppointment?.student;
                } else {
                    return (parseInt(s.is_mp_student) === 1 && parseInt(s.mp_active) === 1)
                        || s.user_id === selectedAppointment?.student;
                }
            });

            const newStudentOptions = filteredStudents.sort((a, b) => {
                if (a.first_name < b.first_name) return -1;
                else if (a.first_name > b.first_name) return 1;
                return 0;
            }).map(s => {
                const relStudent = newStudentUsers.find(su => su.id === s.user_id) || {};
                s.email = relStudent.email;
                return { value: s.user_id, label: `${s.first_name} ${s.last_name}`, object: s };
            });

            const parentsRes = await fetchMembersMpPermissions({ permissions: 'Parent' });
            const newParents = extractArray(parentsRes, ['members', 'parents', 'users']);

            // For ReadingPlex, fetch contracts for students
            let newContractOptions = [{ value: -1, label: 'No Contract' }];
            let newAllContracts = [];
            let newContractsByStudent = {};
            if (isReadingPlex) {
                try {
                    // Use fetchContractsActiveStudents which fetches all active contracts
                    const contractsRes = await fetchContractsActiveStudents();
                    console.log('[APPOINTMENTS] Raw contracts response:', contractsRes);

                    // The API returns data directly as an array (from tryAction)
                    // Try multiple extraction paths to handle different response formats
                    let contracts = [];
                    if (Array.isArray(contractsRes?.data)) {
                        contracts = contractsRes.data;
                    } else if (Array.isArray(contractsRes?.data?.contracts)) {
                        contracts = contractsRes.data.contracts;
                    } else if (Array.isArray(contractsRes)) {
                        contracts = contractsRes;
                    } else {
                        contracts = extractArray(contractsRes, ['contracts', 'data']);
                    }

                    console.log('[APPOINTMENTS] Extracted contracts array:', contracts.length, 'contracts');
                    if (contracts.length > 0) {
                        console.log('[APPOINTMENTS] Sample contract:', JSON.stringify(contracts[0]));
                        console.log('[APPOINTMENTS] Sample contract student field:', contracts[0].student);
                    }

                    // Log all unique contract types to debug missing 1:1/private contracts
                    const contractTypes = [...new Set(contracts.map(c => c.contract_type || c.type || 'Unknown'))];
                    console.log('[APPOINTMENTS] All contract types found:', contractTypes);

                    // Filter to only current/active contracts (end_date >= today or no end_date)
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const activeContracts = contracts.filter(c => {
                        if (!c.end_date) return true; // No end date = always active
                        const endDate = new Date(c.end_date);
                        return endDate >= today;
                    });

                    console.log('[APPOINTMENTS] Active contracts after date filter:', activeContracts.length);
                    console.log('[APPOINTMENTS] Active contract types:', [...new Set(activeContracts.map(c => c.contract_type || c.type))]);

                    newAllContracts = activeContracts;

                    // Build contract options mapped by student ID (UUID string)
                    // The contract's student field contains the student's user_id as a UUID string
                    activeContracts.forEach(c => {
                        const studentId = String(c.student); // Keep as string, don't parseInt!
                        if (!newContractsByStudent[studentId]) {
                            newContractsByStudent[studentId] = [];
                        }
                        const studentName = filteredStudents.find(s => String(s.user_id) === studentId)?.first_name || 'Unknown';
                        // Format dates properly (mm/dd/yyyy)
                        const startDateFormatted = c.start_date ? formatDate(c.start_date) : 'N/A';
                        const endDateFormatted = c.end_date ? formatDate(c.end_date) : 'N/A';
                        const contractOption = {
                            value: c.id,
                            label: `${c.contract_type || c.type || 'Contract'} (${startDateFormatted} - ${endDateFormatted})`,
                            obj: c,
                            studentId: studentId,
                            studentName: studentName
                        };
                        newContractsByStudent[studentId].push(contractOption);
                    });

                    console.log('[APPOINTMENTS] ContractsByStudent keys (first 10):', Object.keys(newContractsByStudent).slice(0, 10));
                    console.log('[APPOINTMENTS] Student user_ids sample (first 5):', filteredStudents.slice(0, 5).map(s => ({ id: s.user_id, name: s.first_name })));

                    // Debug: Check how many students have contracts vs don't
                    const studentsWithContracts = filteredStudents.filter(s => newContractsByStudent[String(s.user_id)]?.length > 0);
                    const studentsWithoutContracts = filteredStudents.filter(s => !newContractsByStudent[String(s.user_id)]?.length);
                    console.log('[APPOINTMENTS] Students WITH contracts:', studentsWithContracts.length);
                    console.log('[APPOINTMENTS] Students WITHOUT contracts:', studentsWithoutContracts.length);
                    if (studentsWithoutContracts.length > 0 && studentsWithoutContracts.length <= 10) {
                        console.log('[APPOINTMENTS] Students without contracts:', studentsWithoutContracts.map(s => s.first_name));
                    }

                    // Initial contract options show all contracts with student names
                    newContractOptions = [
                        { value: -1, label: 'No Contract' },
                        ...activeContracts.map(c => {
                            const studentId = String(c.student);
                            const studentName = filteredStudents.find(s => String(s.user_id) === studentId)?.first_name || 'Unknown';
                            // Format dates properly (mm/dd/yyyy)
                            const startDateFormatted = c.start_date ? formatDate(c.start_date) : 'N/A';
                            const endDateFormatted = c.end_date ? formatDate(c.end_date) : 'N/A';
                            return {
                                value: c.id,
                                label: `${c.contract_type || c.type || 'Contract'} - ${studentName} (${startDateFormatted} - ${endDateFormatted})`,
                                obj: c,
                                studentId: studentId,
                                studentName: studentName
                            };
                        })
                    ];

                    console.log('[APPOINTMENTS] Loaded contracts:', activeContracts.length, 'for', Object.keys(newContractsByStudent).length, 'students');
                    console.log('[APPOINTMENTS] Contract options count:', newContractOptions.length);
                } catch (e) {
                    console.log('[APPOINTMENTS] Error fetching contracts:', e);
                }
            }

            if (mode !== 'create') {
                const appointmentsRes = await fetchAppointmentsIds({ ids: [selectedAppointment.id] });
                const newAppointment = appointmentsRes.data?.appointments?.[0] || {};
                const relCenter = newCenters.find(c => parseInt(c.id) === parseInt(newAppointment.center)) || {};
                newAppointment.centerName = relCenter.name || `Unable to find center (ID: ${newAppointment.center})`;

                const aptDateTime = new Date(newAppointment.date_time);
                const [year, month, date, hours, minutes] = getymdhms(aptDateTime);
                newAppointment.date = `${year}-${month}-${date} 00:00:00`;
                newAppointment.time = hours * 60 + minutes * 1;

                if (mounted.current) setAppointment(newAppointment);
            }

            if (mounted.current) {
                setParents(newParents);
                setCenterOptions(newCenterOptions);
                setStudentOptions(newStudentOptions);
                setAllStudents(newStudentOptions);
                setGroups(newGroups);
                setGroupOptions(newGroupOptions);
                setContractOptions(newContractOptions);
                setAllContracts(newAllContracts);
                setContractsByStudent(newContractsByStudent);
                setLoaded(true);
            }
        };
        init();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const handleClose = useCallback((changes) => {
        if (changes !== true) changes = false;
        async function close() {
            await onSubmitCallback(changes);
            if (mounted.current) setShowModal(false);
        }
        close();
    }, [onSubmitCallback]);

    // Filter students by group (ReadingPlex-specific)
    const filterStudentsByGroup = useCallback((groupId) => {
        if (!isReadingPlex || groupId === -1) {
            setStudentOptions(allStudents);
            return;
        }

        const selectedGroup = groupOptions.find(g => g.value === groupId);
        if (!selectedGroup) {
            console.log('[APPOINTMENTS] filterStudentsByGroup: Group not found', groupId);
            setStudentOptions(allStudents);
            return;
        }

        // Get students from the group's students array (now properly mapped from groupMembers)
        const groupStudentIds = selectedGroup.students || [];

        if (groupStudentIds.length === 0) {
            console.log('[APPOINTMENTS] filterStudentsByGroup: No students in group', groupId, selectedGroup.label);
            // For groups with no students (like 1 on 1 groups that haven't been set up yet), don't show "All Students"
            // Just show all students as individual options
            setStudentOptions(allStudents);
            return;
        }

        console.log('[APPOINTMENTS] filterStudentsByGroup: Group student IDs:', groupStudentIds);
        console.log('[APPOINTMENTS] filterStudentsByGroup: All student values sample:', allStudents.slice(0, 3).map(s => s.value));

        // Match using string comparison for UUIDs - groupStudentIds are already UUID strings
        const filteredStudents = allStudents.filter(s => {
            const studentValue = String(s.value);
            const studentUserId = s.object?.user_id ? String(s.object.user_id) : studentValue;
            return groupStudentIds.some(gid => {
                const gidStr = String(gid);
                return gidStr === studentValue || gidStr === studentUserId;
            });
        });

        console.log('[APPOINTMENTS] filterStudentsByGroup: Filtered', filteredStudents.length, 'students from', allStudents.length, 'for group', selectedGroup.label);

        // If no students matched, don't show "All Students in Group" - just show the filtered list (empty)
        if (filteredStudents.length === 0) {
            console.warn('[APPOINTMENTS] filterStudentsByGroup: No students matched for group', selectedGroup.label);
            setStudentOptions(allStudents); // Fallback to all students if no matches
            return;
        }

        const studentsToShow = filteredStudents;

        // Add "All Students in Group" option at the beginning
        const options = [
            { value: 'all_group', label: '-- All Students in Group --', object: { isAllGroup: true, groupStudents: studentsToShow } },
            ...studentsToShow
        ];

        setStudentOptions(options);
    }, [allStudents, groupOptions]);

    // Get contracts for a specific student (ReadingPlex-specific)
    const getContractsForStudent = useCallback((studentId) => {
        if (!isReadingPlex || !studentId || studentId === -1 || studentId === 'all_group') {
            return [{ value: -1, label: 'No Contract' }];
        }
        // studentId can be a UUID string, so use String() to ensure proper lookup
        const studentContracts = contractsByStudent[String(studentId)] || [];
        console.log('[APPOINTMENTS] getContractsForStudent:', studentId, 'found:', studentContracts.length, 'contracts');
        return [
            { value: -1, label: 'No Contract' },
            ...studentContracts
        ];
    }, [contractsByStudent]);

    // Get common contract for all students in a group (ReadingPlex-specific)
    const getContractsForGroup = useCallback((groupStudents) => {
        if (!isReadingPlex || !groupStudents || groupStudents.length === 0) {
            console.log('[APPOINTMENTS] getContractsForGroup: No students provided');
            return [{ value: -1, label: 'No Contract' }];
        }

        console.log('[APPOINTMENTS] getContractsForGroup: Looking for contracts for', groupStudents.length, 'students');
        console.log('[APPOINTMENTS] getContractsForGroup: ContractsByStudent keys:', Object.keys(contractsByStudent).slice(0, 5));

        // Find contracts for all students in the group
        const allGroupContracts = [];
        groupStudents.forEach(s => {
            // s.value is the student's user_id (UUID string)
            // Also try s.object.user_id as fallback
            const studentId = String(s.value);
            const altStudentId = s.object?.user_id ? String(s.object.user_id) : null;

            let studentContracts = contractsByStudent[studentId] || [];

            // Try alternative ID if primary didn't find contracts
            if (studentContracts.length === 0 && altStudentId && altStudentId !== studentId) {
                studentContracts = contractsByStudent[altStudentId] || [];
            }

            if (studentContracts.length > 0) {
                console.log('[APPOINTMENTS] getContractsForGroup: Found', studentContracts.length, 'contracts for student', studentId);
            }
            allGroupContracts.push(...studentContracts);
        });
        console.log('[APPOINTMENTS] getContractsForGroup: Total contracts found:', allGroupContracts.length);

        // Remove duplicates by contract ID
        const uniqueContracts = [];
        const seenIds = new Set();
        allGroupContracts.forEach(c => {
            if (!seenIds.has(c.value)) {
                seenIds.add(c.value);
                uniqueContracts.push(c);
            }
        });

        console.log('[APPOINTMENTS] getContractsForGroup: Unique contracts:', uniqueContracts.length);

        if (uniqueContracts.length === 0) {
            return [{ value: -1, label: 'No Contract' }];
        }

        return [
            { value: -1, label: 'No Contract' },
            ...uniqueContracts
        ];
    }, [contractsByStudent]);

    const forceDelete = useCallback((formik) => {
        (async function () {
            const { setStatus, setSubmitting } = formik;

            if (mounted.current) setSubmitting(true);

            const response = await deleteAppointmentForce({ id: appointment.id });
            const responseValid = checkResponse(response, mounted, setStatus);
            if (!responseValid && mounted.current) {
                setSubmitting(false);
                return;
            }

            if (mounted.current) setSubmitted(true);
            setTimeout(() => handleClose(true), 1000);
        })();
    }, [deleteAppointmentForce, appointment, handleClose])
    const handleSubmit = useCallback((values, actions) => {
        async function submit() {
            const { setStatus, setSubmitting } = actions;

            if (mounted.current) setSubmitting(true);

            let response = { status: 999 };

            if (['create', 'edit'].includes(mode)) {
                const [year, month, date] = getymd(`${values.date} 00:00:00`);
                const hours = Math.floor(parseInt(values.time.raw) / 60);
                const minutes = parseInt(values.time.raw % 60);
                const dateTime = new Date(`${year}-${month}-${date} ${hours}:${minutes}:00`);

                // Check if "All Students in Group" was selected
                if (values.student?.value === 'all_group' && values.student?.object?.isAllGroup) {
                    // ReadingPlex Group Appointments - Send ONE request with ALL students
                    const groupStudents = values.student.object.groupStudents || [];

                    // Build selectedContracts object from studentContracts state (user-selected per student)
                    // CRITICAL: Use ref to get current value and avoid stale closure
                    const currentStudentContracts = studentContractsRef.current;
                    console.log('[APPOINTMENTS] Current studentContracts state:', studentContracts);
                    console.log('[APPOINTMENTS] Current studentContracts REF:', currentStudentContracts);
                    console.log('[APPOINTMENTS] studentContracts state keys:', Object.keys(studentContracts));
                    console.log('[APPOINTMENTS] studentContracts ref keys:', Object.keys(currentStudentContracts));
                    console.log('[APPOINTMENTS] Group students:', groupStudents);

                    const selectedContractsObj = {};
                    groupStudents.forEach(studentOpt => {
                        const studentId = studentOpt.value;
                        const selectedContract = currentStudentContracts[studentId]; // Use ref value instead of state
                        console.log('[APPOINTMENTS] Building selectedContracts for student:', studentId, 'selected:', selectedContract);
                        console.log('[APPOINTMENTS] Selected contract full object:', JSON.stringify(selectedContract, null, 2));

                        if (selectedContract && selectedContract.obj) {
                            // Use the user-selected contract (note: field is .obj not .object)
                            console.log('[APPOINTMENTS] Using selectedContract.obj for', studentId);
                            selectedContractsObj[studentId] = {
                                obj: selectedContract.obj
                            };
                        } else if (selectedContract && selectedContract.object) {
                            // Fallback for .object field (backward compatibility)
                            console.log('[APPOINTMENTS] Using selectedContract.object for', studentId);
                            selectedContractsObj[studentId] = {
                                obj: selectedContract.object
                            };
                        } else {
                            // Fallback: try to use first contract from contractsByStudent
                            console.log('[APPOINTMENTS] No selected contract, trying fallback from contractsByStudent for', studentId);
                            const availableContracts = contractsByStudent[studentId] || [];
                            console.log('[APPOINTMENTS] Available contracts from contractsByStudent:', availableContracts.length);
                            if (availableContracts.length > 0 && availableContracts[0].obj) {
                                console.log('[APPOINTMENTS] Using fallback contract for', studentId);
                                selectedContractsObj[studentId] = {
                                    obj: availableContracts[0].obj
                                };
                            } else {
                                console.log('[APPOINTMENTS] WARNING: No contract available for', studentId);
                            }
                        }
                    });
                    console.log('[APPOINTMENTS] Final selectedContractsObj:', selectedContractsObj);
                    console.log('[APPOINTMENTS] Number of students with contracts:', Object.keys(selectedContractsObj).length);

                    const appointmentObject = {
                        dateTime,
                        student: 'all', // Required for group appointments
                        groupId: values.group?.value || -1,
                        selectedContracts: selectedContractsObj,
                        center: values.center?.value,
                        duration: values.duration?.value,
                        specialNotes: values.specialNotes || '',
                        specialNotes2: values.specialNotes2 || '',
                        status: values.status?.value || 'Scheduled',
                        nWeeks: values.nWeeks || 1
                    };

                    console.log('[APPOINTMENTS] Sending appointment object:', JSON.stringify(appointmentObject, null, 2));
                    response = await createAppointment(appointmentObject);

                    console.log('[APPOINTMENTS] Group appointment response:', response);
                    console.log('[APPOINTMENTS] Response status:', response?.status);

                    if (!response || response.status >= 400) {
                        setStatus('Some appointments could not be created');
                        if (mounted.current) setSubmitting(false);
                        return;
                    }

                    // Ensure we mark as successful even if status is 201 (Created)
                    if (response.status === 201 || response.status === 200) {
                        console.log('[APPOINTMENTS] Group appointments created successfully');
                    }
                } else {
                    // Single student appointment
                    const appointmentObject = {
                        dateTime,
                        parent: values.student?.object?.parent,
                        student: values.student?.value,
                        center: values.center?.value,
                        duration: values.duration?.value,
                        specialNotes: values.specialNotes || '',
                        specialNotes2: values.specialNotes2 || '',
                        status: values.status?.value || 'Scheduled',
                    };

                    // MathPlex-specific: weight field
                    if (!isReadingPlex) {
                        appointmentObject.weight = values.weight?.value || 1;
                    }

                    // ReadingPlex-specific fields
                    if (isReadingPlex) {
                        console.log('[INDIVIDUAL_SUBMIT] values.contract:', values.contract);
                        console.log('[INDIVIDUAL_SUBMIT] values.contract?.value:', values.contract?.value);
                        console.log('[INDIVIDUAL_SUBMIT] values.contract?.obj:', values.contract?.obj);
                        // Handle "No Contract" - normalize 0 and -1 to 0 for backend
                        const contractValue = values.contract?.value;
                        if (contractValue === 0 || contractValue === -1 || contractValue === '0' || contractValue === '-1') {
                            appointmentObject.contract = 0; // Explicitly send 0 for "No Contract"
                        } else if (contractValue && contractValue > 0) {
                            appointmentObject.contract = parseInt(contractValue);
                        } else {
                            appointmentObject.contract = 0; // Default to "No Contract" if undefined/null
                        }
                        appointmentObject.groupId = values.group?.value || -1;
                        appointmentObject.nWeeks = values.nWeeks || 1;
                        console.log('[INDIVIDUAL_SUBMIT] Sending contract ID:', appointmentObject.contract);
                    }

                    if (mode === 'create') {
                        response = await createAppointment(appointmentObject);
                    } else if (mode === 'edit') {
                        appointmentObject.id = appointment.id;
                        // Ensure contract is included in update
                        if (isReadingPlex) {
                            const contractValue = values.contract?.value;
                            if (contractValue === 0 || contractValue === -1 || contractValue === '0' || contractValue === '-1') {
                                appointmentObject.contract = 0;
                            } else if (contractValue && contractValue > 0) {
                                appointmentObject.contract = parseInt(contractValue);
                            } else {
                                // If contract not provided, keep original
                                appointmentObject.contract = appointment.contract || 0;
                            }
                            console.log('[EDIT_SUBMIT] Sending contract ID:', appointmentObject.contract);
                        }
                        response = await updateAppointmentCore(appointmentObject);
                    }
                }
            } else if (mode === 'delete') {
                response = await deleteAppointment({ id: appointment.id });
            }

            const responseValid = checkResponse(response, mounted, setStatus);
            if (!responseValid && mounted.current) {
                setSubmitting(false);
                return;
            }

            if (mounted.current) setSubmitted(true);
            setTimeout(() => handleClose(true), 1000);
        }
        submit();
    }, [mode, createAppointment, updateAppointmentCore, deleteAppointment, handleClose, appointment]);

    return (
        <Modal show={showModal} onHide={handleClose}>
            <Modal.Header>
                <h2>Appointment Form</h2>
            </Modal.Header>
            <AppointmentModalBodyFooter
                mode={mode}
                selectedDate={selectedDate}
                selectedCenter={selectedCenter}
                selectedAppointment={appointment}
                centerOptions={centerOptions}
                studentOptions={studentOptions}
                groupOptions={groupOptions}
                groups={groups}
                contractOptions={contractOptions}
                contractsByStudent={contractsByStudent}
                studentContracts={studentContracts}
                setStudentContracts={setStudentContracts}
                parents={parents}
                loaded={loaded}
                submitted={submitted}
                handleClose={handleClose}
                handleSubmit={handleSubmit}
                forceDelete={forceDelete}
                filterStudentsByGroup={filterStudentsByGroup}
                getContractsForStudent={getContractsForStudent}
                getContractsForGroup={getContractsForGroup}
                isReadingPlex={isReadingPlex}
            />
        </Modal>
    );
}

export default connect(null, {
    fetchMpCentersAll,
    fetchRpCentersAll,
    fetchStudentsAll,
    fetchMembersMpPermissions,
    fetchAppointmentsIds,
    fetchCycleGroupsActive,
    fetchContractsActiveStudents,
    createAppointment,
    updateAppointmentCore,
    deleteAppointment,
    deleteAppointmentForce
})(AppointmentModal);
